<template>
  <div id="gateway-login" class="page">
    <div id="wallpaper">
      <figure v-on:click="autoFocus"></figure>
    </div>
    <spinner v-if="fetching"/>
    <div v-else id="panel">
      <div id="login">

        <header>
          <a v-bind:href="links.productSite" tabindex="-1" target="_blank">
            <img v-bind:src="assets.logoUrl" alt="" v-bind:title="strings.productName" id="logo"/>
          </a>
          <h1>{{ strings.productName }}</h1>
        </header>

        <main id="actions">
          <form id="credentials" @submit.prevent="onSubmit">
            <section class="status">
              <figure class="progress-bar-infinite" v-if="authenticating"></figure>
              <p class="message">
                <span v-for="(error, index) in errors" :key="index">{{ error }}</span>
              </p>
            </section>
            <label id="username" class="gateway-textbox" v-bind:title="strings.emailTitle">
              <!-- <icon-user></icon-user> -->
              <span>{{ strings.email }}</span>
              <input name="username" ref="username" type="text" v-bind:aria-label="strings.email" value="" spellcheck="false" autocorrect="off" autocapitalize="off" v-model="username" v-bind:disabled="authenticating">
            </label>
            <label id="password" class="gateway-textbox" v-bind:title="strings.passwordTitle">
              <!-- <icon-password></icon-password> -->
              <span>{{ strings.password }}</span>
              <input name="password" ref="password" type="password" v-bind:aria-label="strings.password" value="" v-model="password" v-bind:disabled="authenticating">
            </label>
            <label id="remember" class="gateway-checkbox" v-bind:title="strings.rememberMeTitle">
              <input name="remember" type="checkbox" v-model="rememberMe" v-on:change="toggleRememberMe" v-bind:disabled="authenticating">
              <span>{{ strings.rememberMe }}</span>
              <figure></figure>
            </label>
            <ul>
              <li>
                <router-link to="forgot-password" id="forgot" v-bind:title="strings.forgotPasswordTitle">{{ strings.forgotPassword }}</router-link>
              </li>
            </ul>
            <button id="submit" type="submit" ref="login" v-bind:title="strings.loginButtonTitle" v-bind:aria-label="strings.loginButton" v-bind:disabled="authenticating">{{ strings.loginButton }}<span class="btn-square"><i class="arrow-forwards"></i></span></button>
          </form>

          <p v-if="ssoReady" id="alternatives"><span>{{ strings.loginAlternativesText }}</span></p>

          <form v-if="ssoReady" id="sso" @submit.prevent="onSSOSubmit">
            <button id="provider" type="submit" data-gateway="" v-bind:title="strings.ssoButtonTooltip" v-bind:aria-label="strings.ssoButton">{{ strings.ssoButton }}</button>
          </form>

        </main>

        <footer>
          <!-- <div id="languages"><a href="">Language: <span>en</span></a></div> -->
          <ul id="legal">
            <li><a v-bind:href="links.help" v-bind:title="strings.helpTitle" target="_blank">{{ strings.help }}</a></li>
            <li><a v-bind:href="links.privacyPolicy" v-bind:title="strings.privacyPolicyTitle" target="_blank">{{ strings.privacyPolicy }}</a></li>
            <li><span v-bind:title="strings.trademarkTitle">{{ strings.copyright }}</span></li>
          </ul>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import { CONST } from '@/utils/constants'
import { URLS } from '@/utils/urls'

import router from '@/router'
import navigation from '@/utils/navigation'
import { removeQueryParam } from '@/utils'

import { mapGetters, mapActions } from 'vuex'

import Spinner from '@/components/common/Spinner'
import iconUser from '@/assets/icons/hubble-gateway.svg?user'
import iconPassword from '@/assets/icons/hubble-gateway.svg?password'

export default {
  data () {
    return {
      username: '',
      password: '',
      rememberMe: false,
      rememberMeCookieName: CONST.Cookies.Authentication.RememberMe,
      rememberMeCookieDuration: CONST.Cookies.Settings.LifeSpan,
      assets: {
        logoUrl: `${process.env.BASE_URL}assets\\images\\insightsoftware-hubble-white.svg`,
      },
      strings: {
        productName: this.translate('product name'),
        email: this.translate('email'),
        emailTitle: this.translate('email'),
        password: this.translate('password'),
        passwordTitle: this.translate('password'),
        rememberMe: this.translate('remember me'),
        rememberMeTitle: this.translate('remember me title'),
        forgotPassword: this.translate('forgot password'),
        forgotPasswordTitle: this.translate('forgot password title'),
        loginButton: this.translate('log in'),
        loginButtonTitle: this.translate('log in button title'),
        loginAlternativesText: this.translate('login alternatives text'),
        ssoButton: this.translate('sso button'),
        ssoButtonTooltip: this.translate('sso button tooltip'),
        help: this.translate('help'),
        helpTitle: this.translate('help and support'),
        privacyPolicy: this.translate('privacy'),
        privacyPolicyTitle: this.translate('privacy policy'),
        copyright: this.translate('copyright', { 'year': new Date().getFullYear() }),
        trademarkTitle: this.translate('trademark', { 'year': new Date().getFullYear() }),
        urlProvidedErrorMessage: this.translate('login generic error'),
        urlProvidedTimeoutMessage: this.translate('session expired message'),
        metadataEndpointErrorMessage: this.translate('metadata endpoint unavailable'),
        ssoMisconfigurationErrorMessage: this.translate('sso misconfiguration message'),
      },
      links: {
        forgotPassword: URLS.Public.ForgotPassword(),
        help: URLS.Links.Help(),
        privacyPolicy: URLS.Links.PrivacyPolicy(),
        productSite: URLS.Links.ProductSite(),
        corporateSite: URLS.Links.CorporateSite(),
      }
    }
  },
  components: {
    'icon-user': iconUser,
    'icon-password': iconPassword,
    'spinner': Spinner
  },
  props: {
    urlProvidedError: {
      type: Boolean,
      required: false
    },
    urlProvidedTimeout: {
      type: Boolean,
      required: false
    }
  },
  computed: {
    ...mapGetters({
      errors: 'user/errors',
      loginRedirect: 'user/loginRedirect',
      authenticating: 'user/authenticating',
      startPage: 'user/startPage',
      ssoEnabled: 'metadata/ssoEnabled',
      ssoUrl: 'metadata/ssoUrl',
      fetching:'metadata/fetching'
    }),
    loginText () {
      return this.authenticating ? this.translate('logging in') : this.translate('log in')
    },
    ssoReady () {
      return (this.ssoEnabled && this.ssoUrl)
    },
    ssoUrlQueryParam () {
      return encodeURIComponent(window.location.href)
    }
  },
  methods: {
    ...mapActions('user', [
      'raiseGeneralError',
      'raiseLoginError',
      'login'
    ]),
    ...mapActions('metadata', [
      'retrieveMetadata'
    ]),
    handleUrlProvidedErrors () {
      // Handled
      let handled = false
      // Handlers (?error overrides ?timeout)
      handled = (!handled && this.urlProvidedError) ? this.raiseLoginError([ this.strings.urlProvidedErrorMessage ]) : handled
      handled = (!handled && this.urlProvidedTimeout) ? this.raiseLoginError([ this.strings.urlProvidedTimeoutMessage ]) : handled
      // Only Clean and Refresh when Handled
      if (handled) {
        // Remove Transient Query Params
        const cleanedQuery = removeQueryParam(this.$route.query, ['error', 'timeout'])
        // Rewrite the URL (without navigating)
        router.replace({ query: cleanedQuery })
      }
    },
    async loadMetadata () {
      try {
        // Attempt to get Metadata
        await this.retrieveMetadata()
        // Check configuration
        if (this.ssoEnabled && this.ssoUrl === null) {
          // Raise a Server Error
          this.raiseGeneralError([ this.strings.ssoMisconfigurationErrorMessage ])
        }
      } catch (err) {
        // Raise a Server Error
        this.raiseGeneralError([ this.strings.metadataEndpointErrorMessage ])
      }
    },
    localValidation () {
      const errors = []
      const blankUsername = (this.username.trim().length === 0)
      const blankPassword = (this.password.length === 0)
      if (blankUsername) {
        errors.push(this.translate('login validation blank username'))
      }
      if (blankPassword) {
        errors.push(this.translate('login validation blank password'))
      }
      return errors
    },
    async onSubmit () {
      let localErrors = this.localValidation()
      if (localErrors.length > 0) {
        this.raiseLoginError(localErrors)
        this.autoFocus()
      } else {
        try {
          // Attempt Login
          await this.login({ username: this.username, password: this.password })
          // Redirect straight away or use Router Navigation Guard to Handle Destination
          navigation.redirectOrRefresh(this.loginRedirect)
        } catch (err) {
          // Focus Password Field onFailure
          this.selectElementText(this.$refs.password)
        }
      }
      this.toggleRememberMe()
    },
    onSSOSubmit () {
      navigation.redirect(`${this.ssoUrl}?returnUrl=${this.ssoUrlQueryParam}`)
    },
    autoFocus () {
      const hasUsername = (this.username.length > 0)
      const hasPassword = (this.password.length > 0)
      let target = (!hasUsername) ? this.$refs.username : (!hasPassword) ? this.$refs.password : this.$refs.login
      target.focus()
    },
    selectElementText (element) {
      element.focus()
      element.selectionStart = 0
      element.selectionEnd = element.value.length
    },
    toggleRememberMe () {
      if (this.rememberMe) {
        this.$cookie.set(this.rememberMeCookieName, this.username, { expires: this.rememberMeCookieDuration })
      } else {
        this.$cookie.delete(this.rememberMeCookieName)
      }
    },
    restoreUser () {
      const rememberedUser = this.$cookie.get(this.rememberMeCookieName) || ''
      if (rememberedUser.length > 0) {
        this.username = rememberedUser
        this.rememberMe = true
      } else {
        this.rememberMe = false
      }
    },
    translate (string, object) {
      return this.$i18n.translate(string, object)
    }
  },
  beforeMount () {
    // Get Metadata from API
    this.loadMetadata()
  },
  mounted () {
    // Check for the 'error' query param/flag in the URL (i.e.. ?error)
    this.handleUrlProvidedErrors()
    // IE11 / Edge requires a delay (Need a better fix than this!!!)
    setTimeout(() => {
      this.restoreUser()
      this.autoFocus()
    }, 120)
  }
}
</script>

<style lang="scss">
@import "@/styles/views/login/_login.scss";
</style>
